import { useState } from 'react';
import { useCart } from '../../hooks/useCart';

export default function ProduitSapinFlammeDoreeLumieres() {
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });

  // Image principale du produit - visible par tous les visiteurs
  const productImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/a9e9180f83d58d660f566f5909876bf9.png';

  const product = {
    id: 'sapin-flamme-doree-lumieres',
    name: 'Sapin de Noël en livre plié – Flamme Dorée & Lumières (pièce unique)',
    price: 30,
    image: productImage,
    category: 'Collection de Noël',
    subcategory: 'Sapins de Noël en livres',
    description: "Sapin décoratif réalisé à partir d'un livre entièrement plié à la main, surmonté d'une flamme décorative dorée torsadée et sublimé par une guirlande lumineuse intégrée.",
    features: [
      'Réalisé à la main à partir d\'un livre recyclé',
      'Flamme décorative dorée torsadée au sommet',
      'Guirlande lumineuse LED intégrée (piles incluses)',
      'Large ruban doré',
      'Hauteur approximative : 25-30 cm',
      'Pièce unique'
    ],
    details: "Ce sapin décoratif est une création artisanale réalisée à partir d'un livre entièrement plié à la main, formant une élégante silhouette en cône. Il est surmonté d'une flamme décorative dorée torsadée et sublimé par une guirlande lumineuse intégrée qui diffuse une lumière douce et chaleureuse. Un large ruban doré vient parfaire l'ensemble avec élégance. Son style spectaculaire et lumineux en fait une pièce maîtresse pour une décoration de Noël poétique, raffinée et chaleureuse. Chaque création est unique, façonnée avec soin dans mon atelier à partir de livres recyclés. Aucun sapin ne peut être reproduit à l'identique.",
    badge: 'Pièce unique ✨',
    badgeColor: 'bg-[#C49B86] text-white',
    isNew: true
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleAddToCart = () => {
    try {
      for (let i = 0; i < quantity; i++) {
        addItem({
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          description: product.description
        });
      }
      showNotification(`${quantity} × ${product.name} ajouté${quantity > 1 ? 's' : ''} au panier !`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Notification toast */}
      {notification.show && (
        <div 
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
          style={{ 
            transform: notification.show ? 'translateX(0)' : 'translateX(100%)',
            opacity: notification.show ? 1 : 0
          }}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <nav className="bg-[#FAF9F8] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <a href="/" className="text-[#B97A72] hover:underline">Accueil</a>
            <span className="text-[#6B5E5A]">/</span>
            <a href="/boutique" className="text-[#B97A72] hover:underline">Boutique</a>
            <span className="text-[#6B5E5A]">/</span>
            <a href="/noel" className="text-[#B97A72] hover:underline">Collection Noël</a>
            <span className="text-[#6B5E5A]">/</span>
            <span className="text-[#6B5E5A]">Sapin Flamme Dorée & Lumières</span>
          </div>
        </div>
      </nav>

      {/* Product Section */}
      <main className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Section */}
            <div className="space-y-4">
              <div className="aspect-square bg-[#FAF9F8] border-2 border-[#B97A72] rounded-2xl overflow-hidden flex items-center justify-center">
                <img
                  src={productImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-6">
                <span className="inline-block bg-[#C49B86] text-white px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Collection de Noël 🎄
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {product.name}
                </h1>
                <div className="flex items-baseline space-x-4 mb-6">
                  <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.price} €
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Description
                </h2>
                <p className="text-[#6B5E5A] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.details}
                </p>
              </div>

              {/* Idéal pour */}
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  🔥🎄 Idéal pour :
                </h3>
                <ul className="space-y-2 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Une décoration de Noël spectaculaire et lumineuse</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Un cadeau artisanal d'exception</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Apporter une touche magique et chaleureuse à un intérieur</span>
                  </li>
                </ul>
              </div>

              {/* Caractéristiques */}
              <div className="bg-[#EBD2CF] rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  ✅ Caractéristiques
                </h3>
                <ul className="space-y-2 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Création 100 % faite main</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Pièce unique</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Livre recyclé – démarche éco-responsable</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B97A72] mr-2 mt-1"></i>
                    <span>Flamme décorative, ruban doré et guirlande lumineuse inclus</span>
                  </li>
                </ul>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <label className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité :
                  </label>
                  <div className="flex items-center border-2 border-[#B97A72] rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="px-6 py-2 text-[#6B5E5A] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-4 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  className="w-full bg-[#B97A72] text-white px-8 py-4 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer text-lg font-medium"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Ajouter au panier
                </button>

                <a
                  href="/panier"
                  className="block w-full text-center bg-transparent text-[#B97A72] px-8 py-4 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-lg font-medium"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Voir le panier
                </a>
              </div>

              {/* Info supplémentaire */}
              <div className="mt-8 p-4 bg-[#EBD2CF] rounded-lg">
                <p className="text-[#6B5E5A] text-sm flex items-start" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <i className="ri-information-line text-[#B97A72] mr-2 mt-1"></i>
                  <span>Chaque création est unique. Les détails peuvent légèrement varier d'un modèle à l'autre.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
